import { useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { gptEngineStore } from "../stores/engineStore";
import {
  DefaultChatTransport,
  isToolUIPart,
  getToolName,
  type UIMessage,
} from "ai";
import { z } from "zod";
import { apiBaseUrl, type Api } from "../../../services/api";
import {
  streamFailureRecorder,
  type GptStreamFailure,
} from "../../../../../api/src/shared/gpt-stream-failure";
import { getFirebaseToken } from "../../../services/firebase";
import { currentClient } from "../../../common/hooks/useIsDesktopView";
import type { SupportedLanguage } from "../../../interfaces";
import {
  dedupeSourceRefs,
  messageText,
  messageSourceRefs,
  messageActions,
  gptActionFromTool,
  turnSeconds,
  type GptAction,
  type GptSourceRef,
  type GptTurnSuggestion,
  type GptToolName,
  type ConversationMessage,
} from "../transcript";
import type {
  GptToolStepKind,
  GptStreamStepKind,
} from "../../../components/gpt/chat/GPTToolStep";
import { seedId } from "../answer";
import type {
  AssistantDataParts,
  AssistantHistoryMessage,
  AssistantMetadata,
  AssistantTools,
} from "../../../../../api/src/shared/assistant-ui-message";
import { useFirstTokenHaptic } from "./useFirstTokenHaptic";

// Unified runtime for BOTH chat surfaces — the general Juisci GPT chat AND
// Chat-with-Paper (CWYOP) — on the Hono + Vercel AI SDK `streamText` routes.
// The only per-surface differences are the transport (endpoint + request body)
// and, for the shared-conversation fork, the mid-stream conversation id;
// everything else (message shape, sources, tool steps, haptic) is shared.
//
// Two data parts ride the stream:
// - `data-conversation` (transient, shared-fork route only): the pre-generated
//   id of the conversation the fork is being written to — only visible to
//   `onData`.
// - `data-sources` (persistent): the retrieval grounding refs, landing in the
//   assistant message's parts so in-session turns render sources like history.
//
// A third persistent part, `data-trace`, never rides the stream at all: it is
// seeded by `toUIMessage` to carry the action trace of a RESTORED turn, which a
// live turn instead reads off its own tool parts.

// How a user's question reached the agent — mirrors the api
// `gptQuestionOriginSchema` boundary contract: typed vs a tapped suggestion (a
// JuisciGPT landing pill / pre-prompt, a follow-up chip on either surface, a
// Chat-with-Paper template question). A client Zod boundary because it is
// threaded through the AI SDK's per-send `body`, which reaches
// `prepareSendMessagesRequest` as an untyped `Record<string, any>` and must be
// re-validated before it crosses back to the wire.
export const questionOriginSchema = z.enum([
  "typed",
  "landing_suggestion",
  "followup_suggestion",
  "cwop_template",
]);
export type QuestionOrigin = z.infer<typeof questionOriginSchema>;

// The extra `body` a caller passes to `chat.sendMessage(msg, { body })`, carrying
// the question's origin per send (the transport folds it into the request body).
const sendBodySchema = z.object({ origin: questionOriginSchema });

// The Mode Router mode the general stream route accepts (docs/gpt-mode-router.md):
// only `auto`/`general` reach that route (forced Evidence dispatches elsewhere).
const generalSelectedModeSchema = z.enum(["auto", "general"]);

// The origin to persist for this request: the reported one on a real send, or
// `null` on a regenerate (which carries no fresh input method — the server
// reuses the replaced turn's origin).
function requestOrigin(
  trigger: "submit-message" | "regenerate-message",
  body: Record<string, unknown> | undefined
): QuestionOrigin | null {
  return trigger === "regenerate-message"
    ? null
    : sendBodySchema.parse(body).origin;
}

// The selected mode to send. On a real send the caller reports it in `body`; on a
// regenerate the body is absent and the value is irrelevant (the server reuses
// the replaced turn's mode via `lastUserMode`), so `general` is a safe filler.
function requestSelectedMode(
  body: Record<string, unknown> | undefined
): "auto" | "general" {
  const parsed = z
    .object({ selectedMode: generalSelectedModeSchema })
    .safeParse(body);
  return parsed.success ? parsed.data.selectedMode : "general";
}

// The classifier prediction carried on the router redispatch signal — a client
// mirror of the api `gptRouterPredictionSchema`.
export type RouterPrediction = {
  predictedMode: "general" | "evidence" | "find_publication";
  confidence: number;
};

// The legacy engines' tools stream untyped. They are named one by one: an index
// signature would stop `tool-askUser` from narrowing.
type LegacyTools = Record<
  GptToolName | typeof DECLINE_TOOL_NAME | "generateVisualAbstract",
  { input: unknown; output: unknown }
>;

// The metadata is the assistant engine's; a legacy message carries none.
export type GeneralChatUIMessage = UIMessage<
  AssistantMetadata,
  {
    // Emitted AFTER the answer — the grounding the answer actually cited
    // (resolved server-side at finish; empty for a decline / off-topic
    // retrieval). This is the single source of truth the UI renders.
    sources: GptSourceRef[];
    conversation: { id: string };
    // Emitted after the answer (`{ kind: "none" }` on almost every turn): what
    // this turn proposes — curated topics for the user's SAVED interests, or a
    // Room covering the subject. Never both: the server arbitrates, because the
    // composer band holds one widget. Rendering only — nothing is written or
    // joined until the user taps.
    suggestion: GptTurnSuggestion;
    // Mode Router (docs/gpt-mode-router.md): the general engine can answer
    // neither an Evidence turn nor a Search turn over its SSE transport, so on
    // an Auto turn that confidently routes to either it emits THIS instead of an
    // answer — the client redispatches to that engine's blocking endpoint.
    // Transient (routing, not content).
    router: {
      resolvedMode: "evidence" | "find_publication";
      question: string;
      origin: QuestionOrigin;
      routerPrediction: RouterPrediction | null;
    };
    // The turn's action trace as the DATABASE has it, seeded by `toUIMessage`
    // when a conversation is restored. The server never streams this part: a
    // live turn already carries the real tool parts, which say the same thing
    // and say it as it happens. It exists because those parts are the one thing
    // `useChat` cannot rebuild from history — see `uiMessageActions`, which
    // reads the live ones first and falls back here.
    trace: { actions: GptAction[]; seconds: number | null };
    // Assistant engine only (docs in api `assistant-ui-message.ts`).
    progress: AssistantDataParts["progress"];
    phase: AssistantDataParts["phase"];
    draft: AssistantDataParts["draft"];
    question: AssistantDataParts["question"];
    sourceTotal: AssistantDataParts["sourceTotal"];
    followups: AssistantDataParts["followups"];
    seconds: AssistantDataParts["seconds"];
  },
  LegacyTools & AssistantTools
>;

// The decline tool (api `DECLINE_TOOL_NAME`) — a pure runtime signal, not a
// retrieval step. Streamed as a tool part, so the live UI must NOT surface it
// as a "searching…" step the way the real search tools are surfaced.
const DECLINE_TOOL_NAME = "reportNoAnswer";

export type GptChatInstance = ReturnType<typeof useChat<GeneralChatUIMessage>>;

const conversationDataSchema = z.object({ id: z.string() });

// Boundary parse of the `data-router` redispatch signal (untyped in `onData`).
const routerDataSchema = z.object({
  resolvedMode: z.enum(["evidence", "find_publication"]),
  question: z.string(),
  origin: questionOriginSchema,
  routerPrediction: z
    .object({
      predictedMode: z.enum(["general", "evidence", "find_publication"]),
      confidence: z.number(),
    })
    .nullable(),
});
export type RouterRedispatch = z.infer<typeof routerDataSchema>;

// ── Message helpers (shared) ──────────────────────────────────────────────

export function uiMessageText(message: GeneralChatUIMessage): string {
  return message.parts
    .flatMap((part) => (part.type === "text" ? [part.text] : []))
    .join("\n\n");
}

// Live in-session sources come from two part kinds: the server's up-front
// `data-sources` (deterministic pre-fetch / CWYOP paper injection) and the
// agentic tool parts (searchInternalCorpus / searchPubMed) once they reach
// `output-available` — the latter is the live carrier of PubMed sources.
export function uiMessageSources(
  message: GeneralChatUIMessage
): GptSourceRef[] {
  // Sources come ONLY from the server's `data-sources` part (the cited
  // grounding, emitted at finish). Live tool parts still drive the "searching…"
  // step indicator, but their raw retrieval is NOT surfaced as sources — only
  // what the answer cited is.
  return dedupeSourceRefs(
    message.parts.flatMap((part) =>
      part.type === "data-sources" ? part.data : []
    )
  );
}

// Boundary parse of a LIVE tool call's arguments. The AI SDK types a dynamic
// tool part's `input` as `unknown`, and the trace prints exactly one field out
// of it — the same one the server lifts out of the archived input at read time
// (api `gpt-transcript.ts`), so a turn reads identically live and reloaded.
const toolQuerySchema = z.object({ query: z.string() });

/**
 * The turn's action trace (§ "what the agent did"): the live tool parts while
 * the turn is in this session's memory, the archived trace seeded by
 * `toUIMessage` once it has been reloaded from the database.
 *
 * Live wins, and not arbitrarily: a turn being streamed right now has tool
 * parts and no seeded part, and a turn seeded from history has the seeded part
 * and no tool parts. The two never disagree — they are the same calls, read
 * from the two places they are recorded.
 */
// Each action carries its call's state: the live block shows the calls still running.
export function uiMessageActions(message: GeneralChatUIMessage): {
  actions: Array<
    GptAction & { state: AssistantDataParts["progress"]["state"] }
  >;
  seconds: number | null;
} {
  // The assistant engine's reading of the question comes first, as it came before the research.
  const question = message.parts
    .flatMap((part) => (part.type === "data-question" ? [part.data] : []))
    .at(-1);
  const understood = [
    ...(question === undefined
      ? []
      : [
          { kind: "question" as const, query: question.reformulation },
          ...question.assumptions.map((assumption) => ({
            kind: "assumption" as const,
            query: assumption,
          })),
        ]),
    // Each question the reader answered, which the research then works from.
    ...uiMessageCards(message).flatMap((card) =>
      card.answer === null
        ? []
        : [{ kind: "clarification" as const, query: card.input.question }]
    ),
  ].map((action) => ({ ...action, state: "done" as const }));
  const live = message.parts.flatMap((part) => {
    if (!isToolUIPart(part)) return [];
    const parsed = toolQuerySchema.safeParse(part.input);
    const action = gptActionFromTool(
      getToolName(part),
      parsed.success ? parsed.data.query : null
    );
    if (action === null) return [];
    return [
      {
        ...action,
        state:
          part.state === "input-streaming" || part.state === "input-available"
            ? ("running" as const)
            : ("done" as const),
      },
    ];
  });
  // The assistant engine streams its run's seconds before the finish; a legacy turn has none (`useStreamDuration` times it).
  const streamed = message.parts
    .flatMap((part) => (part.type === "data-seconds" ? [part.data] : []))
    .at(-1);
  const seconds = streamed === undefined ? null : streamed;
  if (live.length > 0) return { actions: [...understood, ...live], seconds };
  const progress = message.parts.flatMap((part) =>
    part.type === "data-progress"
      ? [
          {
            kind: part.data.tool,
            query: part.data.query,
            state: part.data.state,
          },
        ]
      : []
  );
  if (progress.length > 0)
    return { actions: [...understood, ...progress], seconds };
  const seeded = message.parts.flatMap((part) =>
    part.type === "data-trace" ? [part.data] : []
  );
  const last = seeded.at(-1);
  return last === undefined
    ? { actions: understood, seconds }
    : {
        actions: [
          ...understood,
          ...last.actions.map((action) => ({
            ...action,
            state: "done" as const,
          })),
        ],
        seconds: last.seconds,
      };
}

// The assistant engine's stage while it streams. A legacy turn sends none, so it searches until its text arrives.
export function uiMessagePhase(
  message: GeneralChatUIMessage
): AssistantDataParts["phase"] {
  const phase = message.parts
    .flatMap((part) => (part.type === "data-phase" ? [part.data] : []))
    .at(-1);
  return phase === undefined ? "searching" : phase;
}

// The assistant engine's answer while its root writes it; null on a legacy turn and once the run ends.
export function uiMessageDraft(
  message: GeneralChatUIMessage
): AssistantDataParts["draft"] {
  const draft = message.parts
    .flatMap((part) => (part.type === "data-draft" ? [part.data] : []))
    .at(-1);
  return draft === undefined ? null : draft;
}

// The answer has started once the reader sees its text, the assistant engine's draft included.
export function uiMessageHasAnswer(message: GeneralChatUIMessage): boolean {
  return uiMessageText(message) !== "" || uiMessageDraft(message) !== null;
}

// The proposal attached to a turn. Live-only: the server emits it at finish and
// `toUIMessage` deliberately does not rehydrate the archived one, so a reloaded
// conversation carries none.
export function uiMessageSuggestion(
  message: GeneralChatUIMessage
): GptTurnSuggestion {
  const suggestions = message.parts.flatMap((part) =>
    part.type === "data-suggestion" ? [part.data] : []
  );
  const last = suggestions.at(-1);
  return last === undefined ? { kind: "none" } : last;
}

// The phone's rotating step keeps its four kinds: an article read or a similar search shows as PubMed, as before.
// A mission's juisci search shows as a web search.
const ASSISTANT_STEP_KINDS = {
  search: "web",
  library: "internal",
  pubmed: "pubmed",
  web: "web",
  similar: "pubmed",
  read: "pubmed",
  files: "files",
  fileRead: "files",
} as const satisfies Record<
  AssistantDataParts["progress"]["tool"],
  GptToolStepKind
>;

// The assistant engine's question cards, asked or answered, in the order the model asked them.
export function uiMessageCards(message: GeneralChatUIMessage) {
  return message.parts.flatMap((part) => {
    if (part.type !== "tool-askUser") return [];
    // Null while the card waits on the reader.
    const answer = (() => {
      switch (part.state) {
        case "input-available":
          return null;
        case "output-available":
          return part.output.answer;
        // The server sends a card whole and never runs it.
        case "input-streaming":
        case "approval-requested":
        case "approval-responded":
        case "output-error":
        case "output-denied":
          throw new Error(`should not happen: askUser card ${part.state}`);
        default:
          throw new Error(part satisfies never);
      }
    })();
    return [{ toolCallId: part.toolCallId, input: part.input, answer }];
  });
}

// The Questions a message holds: a user message is one, and so is each answered card.
export function uiMessageQuestionCount(message: GeneralChatUIMessage): number {
  if (message.role === "user") return 1;
  return uiMessageCards(message).filter((card) => card.answer !== null).length;
}

// The card an assistant message still waits on.
export function uiMessageRequest(message: GeneralChatUIMessage) {
  const pending = uiMessageCards(message).filter(
    (card) => card.answer === null
  );
  const last = pending.at(-1);
  return last === undefined ? null : last;
}

// The follow-up questions the assistant engine wrote with its answer. Live-only, like the suggestion.
export function uiMessageFollowups(message: GeneralChatUIMessage): string[] {
  const followups = message.parts.flatMap((part) =>
    part.type === "data-followups" ? [part.data] : []
  );
  const last = followups.at(-1);
  return last === undefined ? [] : last;
}

// How many rows the Sources tab of a complete assistant answer lists, which the api sends beside its sources.
export function uiMessageSourceTotal(message: GeneralChatUIMessage): number {
  const total = message.parts
    .flatMap((part) => (part.type === "data-sourceTotal" ? [part.data] : []))
    .at(-1);
  if (total === undefined)
    throw new Error("should not happen: a complete answer without its total");
  return total;
}

// The assistant history's messages, with its trace steps in the trace's shape.
export function assistantUIMessages(
  messages: ReadonlyArray<AssistantHistoryMessage>
): GeneralChatUIMessage[] {
  return messages.map((message) => ({
    ...message,
    parts: message.parts.map((part) =>
      part.type === "data-trace"
        ? {
            ...part,
            data: {
              actions: part.data.actions.map((step) => ({
                kind: step.tool,
                query: step.query,
              })),
              seconds: part.data.seconds,
            },
          }
        : part
    ),
  }));
}

// Live retrieval kinds currently in flight (§5 — "stream tool usage live"). The
// agent calls the search tools in several rounds, so DEDUPE by kind: a single
// "Searching PubMed…" row, never one per call. Ephemeral — only the in-flight
// (`input-*`) calls count, so the indicator follows the live activity and is
// gone once retrieval finishes (and never appears on reloaded history). The
// decline tool is a runtime signal, never a retrieval step.
export function uiActiveToolKinds(
  message: GeneralChatUIMessage
): GptToolStepKind[] {
  const kinds = message.parts.flatMap((part): GptToolStepKind[] => {
    if (part.type === "data-progress")
      return part.data.state === "running"
        ? [ASSISTANT_STEP_KINDS[part.data.tool]]
        : [];
    if (!isToolUIPart(part)) return [];
    // The card waits on the reader, not on a search.
    if (
      getToolName(part) === DECLINE_TOOL_NAME ||
      getToolName(part) === "askUser"
    )
      return [];
    if (part.state !== "input-streaming" && part.state !== "input-available")
      return [];
    const toolName = getToolName(part);
    // Explicit per tool, with the ATTACHMENT tools named: they used to fall into
    // the `internal` default, so a turn spent grepping the user's own
    // bibliography announced "Searching your Juisci library…" — describing work
    // it was not doing, and implying it had ignored the file it was reading.
    return [
      toolName === "searchPubMed"
        ? "pubmed"
        : toolName === "searchScientificWeb"
          ? "web"
          : toolName === "grepFile" ||
              toolName === "readFileLines" ||
              toolName === "searchUploadedFiles" ||
              toolName === "readDocumentPages"
            ? "files"
            : "internal",
    ];
  });
  return [...new Set(kinds)];
}

// DB transcript turn (populated: internal sources hydrated to articles) →
// `useChat` UI message: the text as a text part, the grounding refs as a
// persistent `data-sources` part — the exact shape streamed turns arrive in.
// Used to seed both surfaces from their history endpoint.
export function toUIMessages(
  messages: ReadonlyArray<ConversationMessage>
): GeneralChatUIMessage[] {
  return messages.map((message, index) =>
    toUIMessage(message, index, messages)
  );
}

// Not exported, and takes the WHOLE transcript rather than reading it off
// `map`'s third argument: an assistant turn is timed against the question
// before it, and a caller that mapped this directly could silently drop that
// pairing (and the message id's ordinal with it) by tightening the callback.
function toUIMessage(
  message: ConversationMessage,
  index: number,
  all: ReadonlyArray<ConversationMessage>
): GeneralChatUIMessage {
  const text = messageText(message);
  const sources = messageSourceRefs(message);
  const actions = messageActions(message);
  // `.at` so the miss is a real `undefined` consumed on the spot, and so the
  // first message of a transcript reads as "no question before it" rather than
  // wrapping around to the last one.
  const previous = index === 0 ? undefined : all.at(index - 1);
  const seconds =
    message.role !== "assistant" ||
    previous === undefined ||
    previous.role !== "user"
      ? null
      : turnSeconds(previous, message);
  // The proposal (interest OR room) is deliberately NOT rehydrated. Leaving the
  // conversation counts as dismissing it: the card belongs to the turn that
  // happened in front of the user, and re-presenting it on every later visit
  // would nag — doubly so for a room, which is a partner-supported space. The
  // agent may propose the subject again in a future turn (the already-declared
  // and already-joined filters keep it from re-proposing what was accepted), so
  // nothing is lost. The persisted transcript parts stay as the archival record
  // of what was proposed — like the tool parts, written but not rendered.
  return {
    id: seedId(message.role, index),
    role: message.role,
    parts: [
      ...(text === "" ? [] : [{ type: "text" as const, text }]),
      ...(sources.length === 0
        ? []
        : [{ type: "data-sources" as const, data: sources }]),
      // Seeded on every assistant turn, empty trace included: the part is what
      // carries the DURATION, and an answer that needed no search still took
      // time worth showing. A user message carries neither.
      ...(message.role === "assistant"
        ? [{ type: "data-trace" as const, data: { actions, seconds } }]
        : []),
    ],
  };
}

// Retry after a stream error. The errored turn was never persisted (the
// server's onFinish didn't run), so it lives only in client state: drop it and
// hand the question back to be re-asked, which appends onto the last GOOD turn
// rather than duplicating the question. Null when there is nothing to retry.
// (A COMPLETED answer is replayed via `chat.regenerate` instead, which truncates
// the last turn server-side.)
export function dropTurnToRetry(messages: GeneralChatUIMessage[]): {
  question: string;
  kept: GeneralChatUIMessage[];
} | null {
  let lastUserIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message !== undefined && message.role === "user") {
      lastUserIndex = i;
      break;
    }
  }
  if (lastUserIndex === -1) return null;
  const lastUser = messages.at(lastUserIndex);
  if (lastUser === undefined) return null;
  return {
    question: uiMessageText(lastUser),
    kept: messages.slice(0, lastUserIndex),
  };
}

// After a lost turn: the screen holding the questions the server holds, so the
// `turnIndex` of the next requests counts them, and the answer the server's
// transcript ends with, the landmark of the next turn (see `SentTurn`).
// - A lost question was never persisted: drop it, with any partial answer.
// - A lost Regenerate left the server's turn in place. The screen keeps its
//   question; it may have lost the answer being replaced, which the server
//   still ends with.
export function afterLostTurn(
  messages: GeneralChatUIMessage[],
  lost: { kind: "ask" | "regenerate"; endedWith: string | null }
): { screen: GeneralChatUIMessage[]; endedWith: string | null } {
  switch (lost.kind) {
    case "ask": {
      const dropped = dropTurnToRetry(messages);
      const screen = dropped === null ? messages : dropped.kept;
      return { screen, endedWith: endingAnswerOf(screen) };
    }
    case "regenerate":
      return { screen: messages, endedWith: lost.endedWith };
    default:
      throw new Error(lost.kind satisfies never);
  }
}

// The answer a transcript ENDS with, or null when it does not end with one.
export function endingAnswerOf(
  messages: GeneralChatUIMessage[]
): string | null {
  const last = messages.at(-1);
  if (last === undefined || last.role !== "assistant") return null;
  return uiMessageText(last);
}

// Streaming-state derivation shared by the conversation component, the legacy
// general layout, and the landing loaders. Pure (no hook) — reads `chat`.
//
// `isReconnecting` (from `useGptStreamRecovery`) counts as streaming even though
// the transport has already failed: the ANSWER is still being written, server
// side, and the surface must keep waiting for it rather than hand the composer
// back and invite a second question the server would 409.
export function deriveStreamState(
  chat: Pick<GptChatInstance, "status" | "messages">,
  isReconnecting: boolean
): {
  isStreaming: boolean;
  isAwaitingFirstToken: boolean;
  // The live retrieval rows to show below the turn: one per active search kind
  // (deduped), or `["thinking"]` for the pre-token reasoning phase, or `[]`.
  streamingSteps: GptStreamStepKind[];
} {
  const isStreaming =
    chat.status === "submitted" ||
    chat.status === "streaming" ||
    isReconnecting;
  const last = chat.messages.at(-1);
  const hasAnswerText =
    last !== undefined && last.role === "assistant" && uiMessageHasAnswer(last);
  const isAwaitingFirstToken = isStreaming && !hasAnswerText;
  // The tool parts frozen on the dead stream describe searches that finished (or
  // were abandoned) minutes ago, so they must not be replayed as live activity:
  // reconnecting is its own single step.
  if (isReconnecting)
    return {
      isStreaming,
      isAwaitingFirstToken,
      streamingSteps: ["reconnecting"],
    };
  // Show every search currently in flight (deduped), so a parallel PubMed call
  // is never shadowed by the internal one. Falls back to "thinking" for the
  // pre-token phase before any tool runs. Ephemeral: gone once the answer
  // streams (no active tools + not awaiting).
  const activeKinds =
    isStreaming && last !== undefined && last.role === "assistant"
      ? uiActiveToolKinds(last)
      : [];
  const streamingSteps: GptStreamStepKind[] =
    activeKinds.length > 0
      ? activeKinds
      : isAwaitingFirstToken
        ? ["thinking"]
        : [];
  return { isStreaming, isAwaitingFirstToken, streamingSteps };
}

// ── Transport ─────────────────────────────────────────────────────────────

export type GptChatTransport =
  // Every general-chat turn continues an EXISTING conversation: the JuisciGPT
  // landing mints the row before it hands the question over to the conversation
  // page, so there is no "create as you ask" route left on this surface.
  | {
      kind: "general";
      conversationId: string;
      language: SupportedLanguage;
    }
  // Resume a SHARED conversation ("Reprendre la discussion"): the first
  // question forks the shared transcript into a new conversation owned by the
  // visitor. The forked id arrives mid-stream (data-conversation), then the
  // page navigates to it and later turns ride the general continuation.
  | { kind: "shared-fork"; token: string; language: SupportedLanguage }
  // `roomId` scopes what the turn may CREATE, not what it may read: the agent's
  // `generateVisualAbstract` tool brands and files the poster against this room,
  // and the card that renders it queries by the same (article, room) pair — so a
  // room-scoped chat that generated against `null` would produce a Juisci-branded
  // artifact the room's own conversation then cannot see. Required rather than
  // defaulted, so a new surface has to state which room it speaks for. Ignored
  // on the anonymous route, which creates nothing.
  | {
      kind: "paper";
      articleId: string;
      roomId: string | null;
      isAnonymous: boolean;
    }
  // A LOGGED-OUT visitor's question (`/webapp/public/gpt/stream`): no
  // conversation, no history, nothing persisted, one question per session.
  // `sessionKey` names the session, so asking another question starts a fresh
  // chat instead of appending to the last one. `device` is the browser
  // fingerprint the daily ceilings count on, null when it could not be read.
  | {
      kind: "anonymous";
      sessionKey: string;
      language: SupportedLanguage;
      device: string | null;
    };

// What a transport is bound to: `id` keys the `useChat` store, `variant` the
// rest of what rebuilds the transport.
function transportIdentity(transport: GptChatTransport): {
  id: string;
  variant: string;
} {
  switch (transport.kind) {
    case "general":
      return { id: transport.conversationId, variant: transport.language };
    case "shared-fork":
      return { id: `shared-${transport.token}`, variant: transport.language };
    case "paper":
      return {
        id: transport.articleId,
        variant: String(transport.isAnonymous),
      };
    case "anonymous":
      return {
        id: `anonymous-${transport.sessionKey}`,
        variant: `${transport.language}|${transport.device === null ? "" : transport.device}`,
      };
    default:
      throw new Error(transport satisfies never);
  }
}

// The index, among user turns, of the question a request asks: the server
// replaces its last turn on a regenerate only when that turn is this one, so a
// failed turn it never persisted does not cost the turn before it.
function userTurnIndex(
  messages: ReadonlyArray<Pick<GeneralChatUIMessage, "role">>
): number {
  return messages.filter((message) => message.role === "user").length - 1;
}

function transportFor(
  transport: GptChatTransport,
  fetch: typeof globalThis.fetch
) {
  if (transport.kind === "general") {
    const language = transport.language;
    return new DefaultChatTransport<GeneralChatUIMessage>({
      api: `${apiBaseUrl}/webapp/private/conversations/${transport.conversationId}/stream/v2`,
      fetch,
      // The DB is the source of truth for history; only the new question
      // crosses the wire. Auth token resolved per-request (Firebase rotates).
      prepareSendMessagesRequest: async ({ messages, trigger, body }) => {
        const last = messages.at(-1);
        const question = last === undefined ? "" : uiMessageText(last);
        const token = await getFirebaseToken();
        const headers: Record<string, string> =
          token === null ? {} : { Authorization: `Bearer ${token}` };
        const origin = requestOrigin(trigger, body);
        // `regenerate` drops the last turn server-side. The SDK trims the
        // target assistant message before this runs, so `messages.at(-1)` is
        // already the user question to replay.
        return {
          body: {
            question,
            language,
            regenerate: trigger === "regenerate-message",
            turnIndex: userTurnIndex(messages),
            origin,
            client: currentClient(),
            selectedMode: requestSelectedMode(body),
            // Read per send, not captured at transport construction: switching
            // engine from the console then takes effect on the next question
            // rather than on the next full remount.
            engine: gptEngineStore.get(),
          } satisfies Api["post"]["/webapp/private/conversations/:id/stream/v2"]["input"]["json"],
          headers,
        };
      },
    });
  }
  if (transport.kind === "shared-fork") {
    const language = transport.language;
    return new DefaultChatTransport<GeneralChatUIMessage>({
      api: `${apiBaseUrl}/webapp/private/conversations/shared/${transport.token}/stream`,
      fetch,
      // Like the general create-flow: only the new question crosses the wire —
      // the server rebuilds the shared context from the token.
      prepareSendMessagesRequest: async ({ messages, trigger, body }) => {
        const last = messages.at(-1);
        const question = last === undefined ? "" : uiMessageText(last);
        const token = await getFirebaseToken();
        const headers: Record<string, string> =
          token === null ? {} : { Authorization: `Bearer ${token}` };
        // A shared fork is always a fresh send (no regenerate trigger here), so
        // the input method is always known.
        if (trigger === "regenerate-message")
          throw new Error("should not happen: regenerate on a shared fork");
        return {
          body: {
            question,
            language,
            origin: sendBodySchema.parse(body).origin,
            client: currentClient(),
          } satisfies Api["post"]["/webapp/private/conversations/shared/:token/stream"]["input"]["json"],
          headers,
        };
      },
    });
  }
  if (transport.kind === "anonymous") {
    const language = transport.language;
    const device = transport.device;
    return new DefaultChatTransport<GeneralChatUIMessage>({
      api: `${apiBaseUrl}/webapp/public/gpt/stream`,
      fetch,
      // The visitor cookie the daily ceilings count on. The shared request
      // client omits credentials by default.
      credentials: "include",
      // No token, no history: the route answers one question and saves it
      // under the visitor, for their account to claim.
      prepareSendMessagesRequest: ({ messages }) => {
        const last = messages.at(-1);
        if (last === undefined)
          throw new Error(
            "should not happen: an anonymous send without a question"
          );
        const question = uiMessageText(last);
        return {
          body: {
            question,
            language,
            device,
            client: currentClient(),
          } satisfies Api["post"]["/webapp/public/gpt/stream"]["input"]["json"],
        };
      },
    });
  }
  const isAnonymous = transport.isAnonymous;
  const roomId = transport.roomId;
  const path = isAnonymous
    ? `/webapp/public/articles/${transport.articleId}/chat-with-paper/stream`
    : `/webapp/private/articles/${transport.articleId}/chat-with-paper/stream`;
  return new DefaultChatTransport<GeneralChatUIMessage>({
    api: `${apiBaseUrl}${path}`,
    fetch,
    // DefaultChatTransport already sets Content-Type: application/json — do NOT
    // set it again (a doubled header breaks Hono's JSON body parse).
    prepareSendMessagesRequest: async ({ messages, trigger, body }) => {
      const last = messages.at(-1);
      const query = last === undefined ? "" : uiMessageText(last);
      const token = isAnonymous ? null : await getFirebaseToken();
      const headers: Record<string, string> =
        token === null ? {} : { Authorization: `Bearer ${token}` };
      // The anonymous route persists nothing, so it carries neither the
      // `regenerate` flag nor an origin.
      if (isAnonymous) return { body: { query }, headers };
      // `regenerate` drops the last turn server-side (LLM context + persisted
      // transcript); origin is null on a replay (the server reuses the replaced
      // turn's origin).
      return {
        body: {
          query,
          roomId,
          regenerate: trigger === "regenerate-message",
          turnIndex: userTurnIndex(messages),
          origin: requestOrigin(trigger, body),
          client: currentClient(),
        } satisfies Api["post"]["/webapp/private/articles/:id/chat-with-paper/stream"]["input"]["json"],
        headers,
      };
    },
  });
}

/**
 * `useChat` bound to either chat surface. `transport.kind` selects the
 * endpoints and request body; `onConversationCreated` only fires on the
 * shared-conversation fork (the transient `data-conversation` part never
 * arrives on the general or paper streams). `initialMessages` seeds the DB
 * transcript — pass it only once history has resolved, since it is the hook's
 * initial state.
 */
export function useGptChat(opts: {
  transport: GptChatTransport;
  initialMessages: GeneralChatUIMessage[];
  onConversationCreated?: (conversationId: string) => void;
  // The finished turn's question + answer.
  onTurnFinish?: (question: string, answer: string) => void;
  // A turn that produced no answer, classified at the transport boundary (see
  // `GptStreamFailure`). REQUIRED — a surface has to say what it does about a
  // dropped connection, because the answer may well exist anyway: the general
  // chat hands this to `useGptStreamRecovery`, the surfaces that have no
  // persisted turn to poll for say so with a no-op.
  onFailure: (failure: GptStreamFailure) => void;
  // The server accepted a turn (2xx on the stream request), before any answer.
  // Null when the surface has nothing to refresh at that point.
  onTurnAccepted: (() => void) | null;
  // Mode Router (docs/gpt-mode-router.md): the general engine routed an Auto turn
  // to Evidence or Search. The surface drops the (answerless) general turn and
  // redispatches to that engine. Only the unified conversation page handles this;
  // other surfaces omit it (Auto never routes there).
  onRedispatch?: (redispatch: RouterRedispatch) => void;
}): GptChatInstance {
  const transportInput = opts.transport;
  // Latest-value ref: the transport is memoized on its identity, so it must
  // read the CURRENT handler rather than the one it was built with.
  const onTurnAccepted = useRef(opts.onTurnAccepted);
  onTurnAccepted.current = opts.onTurnAccepted;
  // One per chat, for its whole life: the transport is rebuilt on a new
  // identity, and what it records must still reach this chat's `onError`.
  const [failures] = useState(() =>
    streamFailureRecorder(() => {
      const accepted = onTurnAccepted.current;
      if (accepted !== null) accepted();
    })
  );
  const identity = transportIdentity(transportInput);
  const transport = useMemo(
    () => transportFor(transportInput, failures.fetch),
    [transportInput.kind, identity.id, identity.variant]
  );
  const id = identity.id;

  const onConversationCreated = opts.onConversationCreated;
  const onTurnFinish = opts.onTurnFinish;
  const onFailure = opts.onFailure;
  const onRedispatch = opts.onRedispatch;
  // A router redispatch signalled mid-stream (data-router). Held until the stream
  // closes so the surface's setMessages/redispatch runs from `onFinish`, not
  // while the SDK is still settling this turn. A ref (consumed within the turn,
  // renders nothing).
  const pendingRedispatch = useRef<RouterRedispatch | null>(null);
  // Latest-value ref: `onFinish` fires much later than the render that armed it,
  // and it must call the CURRENT handler (which closes over the current chat /
  // evidence hooks), never a stale one captured when the chat was created.
  const onRedispatchRef = useRef(onRedispatch);
  onRedispatchRef.current = onRedispatch;
  const chat = useChat<GeneralChatUIMessage>({
    id,
    transport,
    messages: opts.initialMessages,
    onData: (part) => {
      if (part.type === "data-router") {
        // Wire boundary: the transient data part arrives untyped in `onData`.
        pendingRedispatch.current = routerDataSchema.parse(part.data);
        return;
      }
      if (part.type !== "data-conversation") return;
      if (onConversationCreated === undefined) return;
      // Wire boundary: the transient data part arrives untyped in `onData`.
      onConversationCreated(conversationDataSchema.parse(part.data).id);
    },
    onFinish: ({ messages, message, isError }) => {
      // A router redispatch: this "turn" carried no answer — it handed the turn
      // to the Evidence or Search engine. Fire the redispatch (the surface drops
      // this empty turn and runs that engine) and skip the finished-turn handling.
      const redispatch = pendingRedispatch.current;
      if (redispatch !== null) {
        pendingRedispatch.current = null;
        const handle = onRedispatchRef.current;
        if (handle !== undefined) handle(redispatch);
        return;
      }
      if (onTurnFinish === undefined) return;
      // The SDK calls `onFinish` from a `finally`, so a FAILED turn reaches it too,
      // carrying whatever half of the answer had arrived. That is not a finished
      // turn: reporting it hands the surface a partial answer to navigate on or
      // refresh follow-ups from — and it would close the recovery window the
      // failure has just opened, on the one turn that needs it. (`isAbort` is NOT
      // filtered: a user-initiated stop keeps its existing hand-off, and the
      // recovery is settled by the stop handlers themselves.)
      if (isError) return;
      const lastUser = messages.filter((m) => m.role === "user").at(-1);
      onTurnFinish(
        lastUser === undefined ? "" : uiMessageText(lastUser),
        uiMessageText(message)
      );
    },
    onError: () => {
      // The `Error` itself is deliberately unused: everything actionable about a
      // failed turn is either the status the transport recorded, or the fact that
      // there was no status at all.
      onFailure(failures.take());
    },
  });

  // Light haptic tick when the assistant starts writing (first text token).
  const last = chat.messages.at(-1);
  useFirstTokenHaptic(
    chat.status === "streaming" &&
      last !== undefined &&
      last.role === "assistant" &&
      uiMessageText(last) !== ""
  );

  return chat;
}
